import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, Search, Star, User, Phone, Mail, Link, IndianRupee, 
  FolderKanban, Clock, AlertTriangle, CheckCircle, Trophy, TrendingUp, BarChart3, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const SPECIALITIES = [
  { value: 'editor', label: 'Editor' },
  { value: 'shooter', label: 'Shooter' },
  { value: 'both', label: 'Both' }
];

const FreelancersPage = () => {
  const { user } = useAuth();
  const [freelancers, setFreelancers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailSheet, setShowDetailSheet] = useState(false);
  const [selectedFreelancer, setSelectedFreelancer] = useState(null);
  const [freelancerPayments, setFreelancerPayments] = useState(null);
  const [freelancerPerf, setFreelancerPerf] = useState(null);
  const [activeTab, setActiveTab] = useState('freelancers');
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [performanceData, setPerformanceData] = useState([]);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', payment_date: '', notes: '' });
  const [newFreelancer, setNewFreelancer] = useState({
    name: '',
    phone: '',
    email: '',
    password: '',
    speciality: 'editor',
    portfolio_link: '',
    rate_per_project: '',
    notes: ''
  });

  const isAdmin = (user?.roles || [user?.role]).includes('admin');

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    try {
      if (activeTab === 'freelancers') {
        const res = await api.get('/freelancers');
        setFreelancers(res.data);
      } else if (activeTab === 'assignments') {
        const res = await api.get('/freelancer-assignments');
        setAssignments(res.data);
      } else if (activeTab === 'performance') {
        const res = await api.get('/freelancers/performance');
        setPerformanceData(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFreelancer = async () => {
    if (!newFreelancer.name || !newFreelancer.email || !newFreelancer.password) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      await api.post('/freelancers', {
        ...newFreelancer,
        rate_per_project: newFreelancer.rate_per_project ? parseFloat(newFreelancer.rate_per_project) : null
      });
      toast.success('Freelancer created successfully');
      setShowCreateDialog(false);
      setNewFreelancer({
        name: '',
        phone: '',
        email: '',
        password: '',
        speciality: 'editor',
        portfolio_link: '',
        rate_per_project: '',
        notes: ''
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create freelancer');
    }
  };

  const handleFreelancerClick = async (freelancer) => {
    setSelectedFreelancer(freelancer);
    setFreelancerPerf(null);
    setFreelancerPayments(null);
    setShowDetailSheet(true);
    try {
      const [paymentsRes, perfRes] = await Promise.all([
        api.get(`/freelancers/${freelancer.id}/payments`),
        api.get('/freelancers/performance')
      ]);
      setFreelancerPayments(paymentsRes.data);
      const myPerf = perfRes.data.find(p => p.id === freelancer.id);
      if (myPerf) setFreelancerPerf(myPerf);
    } catch (err) {
      console.error('Failed to fetch freelancer details:', err);
    }
  };

  const handleUpdateRating = async (freelancerId, rating) => {
    try {
      await api.patch(`/freelancers/${freelancerId}`, { internal_rating: rating });
      toast.success('Rating updated');
      setFreelancers(freelancers.map(f => 
        f.id === freelancerId ? {...f, internal_rating: rating} : f
      ));
      if (selectedFreelancer?.id === freelancerId) {
        setSelectedFreelancer({...selectedFreelancer, internal_rating: rating});
      }
    } catch (err) {
      toast.error('Failed to update rating');
    }
  };

  const handleAddPayment = async () => {
    if (!paymentForm.amount || !paymentForm.payment_date) {
      toast.error('Please fill in amount and date');
      return;
    }
    try {
      await api.post(`/freelancer-assignments/${selectedAssignment.id}/payments`, {
        amount: parseFloat(paymentForm.amount),
        payment_date: new Date(paymentForm.payment_date).toISOString(),
        notes: paymentForm.notes || null
      });
      toast.success('Payment recorded');
      setShowPaymentDialog(false);
      setPaymentForm({ amount: '', payment_date: '', notes: '' });
      setSelectedAssignment(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add payment');
    }
  };

  const handleUpdateAssignmentStatus = async (assignmentId, newStatus) => {
    try {
      await api.patch(`/freelancer-assignments/${assignmentId}/status`, { status: newStatus });
      toast.success('Status updated');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const filteredFreelancers = freelancers.filter(f =>
    f.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    f.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getReliabilityColor = (score) => {
    if (score >= 90) return 'text-emerald-400';
    if (score >= 70) return 'text-yellow-400';
    return 'text-rose-400';
  };

  const getTrackingBadge = (status) => {
    const badges = {
      on_track: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      at_risk: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
      late: 'bg-rose-500/10 text-rose-400 border-rose-500/20'
    };
    return badges[status] || badges.on_track;
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending_confirmation: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      confirmed: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      in_progress: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
      delivered: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      late: 'bg-rose-500/10 text-rose-400 border-rose-500/20'
    };
    return badges[status] || badges.pending_confirmation;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="freelancers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">Freelancers</h1>
          <p className="text-zinc-500 text-sm mt-1">
            {freelancers.length} freelancers, {assignments.filter(a => a.status === 'in_progress' || a.status === 'confirmed').length} active assignments
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}
            className="border-[#F5A623]/30 text-[#F5A623] hover:bg-[#F5A623]/10" data-testid="refresh-freelancers">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <Button 
            className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black" 
            data-testid="add-freelancer-btn"
            onClick={() => setShowCreateDialog(true)}
          >
            <Plus className="h-4 w-4 mr-2" /> Add Freelancer
          </Button>
          <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle>Add New Freelancer</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4 max-h-[70vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Name *</Label>
                  <Input
                    value={newFreelancer.name}
                    onChange={(e) => setNewFreelancer({...newFreelancer, name: e.target.value})}
                    className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                    placeholder="Full Name"
                    data-testid="freelancer-name-input"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Phone</Label>
                  <Input
                    value={newFreelancer.phone}
                    onChange={(e) => setNewFreelancer({...newFreelancer, phone: e.target.value})}
                    className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                    placeholder="+91 9876543210"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Email *</Label>
                  <Input
                    type="email"
                    value={newFreelancer.email}
                    onChange={(e) => setNewFreelancer({...newFreelancer, email: e.target.value})}
                    className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                    placeholder="freelancer@email.com"
                    data-testid="freelancer-email-input"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Password *</Label>
                  <Input
                    type="password"
                    value={newFreelancer.password}
                    onChange={(e) => setNewFreelancer({...newFreelancer, password: e.target.value})}
                    className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                    placeholder="••••••••"
                    data-testid="freelancer-password-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Speciality</Label>
                  <Select value={newFreelancer.speciality} onValueChange={(v) => setNewFreelancer({...newFreelancer, speciality: v})}>
                    <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10">
                      {SPECIALITIES.map(s => (
                        <SelectItem key={s.value} value={s.value} className="text-white hover:bg-white/10">{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Rate per Project (₹)</Label>
                  <Input
                    type="number"
                    value={newFreelancer.rate_per_project}
                    onChange={(e) => setNewFreelancer({...newFreelancer, rate_per_project: e.target.value})}
                    className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                    placeholder="15000"
                  />
                </div>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Portfolio Link</Label>
                <Input
                  value={newFreelancer.portfolio_link}
                  onChange={(e) => setNewFreelancer({...newFreelancer, portfolio_link: e.target.value})}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                  placeholder="https://portfolio.com"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Notes</Label>
                <Textarea
                  value={newFreelancer.notes}
                  onChange={(e) => setNewFreelancer({...newFreelancer, notes: e.target.value})}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                  placeholder="Internal notes about this freelancer..."
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-white/10 text-white hover:bg-white/5">
                  Cancel
                </Button>
                <Button onClick={handleCreateFreelancer} className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black" data-testid="submit-freelancer-btn">
                  Add Freelancer
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-[#121212] border border-white/10 p-1">
          <TabsTrigger 
            value="freelancers" 
            className="data-[state=active]:bg-[#F5A623] data-[state=active]:text-black text-zinc-400"
            data-testid="freelancers-tab"
          >
            Freelancers ({freelancers.length})
          </TabsTrigger>
          <TabsTrigger 
            value="assignments" 
            className="data-[state=active]:bg-[#F5A623] data-[state=active]:text-black text-zinc-400"
            data-testid="assignments-tab"
          >
            Assignments ({assignments.length})
          </TabsTrigger>
          <TabsTrigger 
            value="performance" 
            className="data-[state=active]:bg-[#F5A623] data-[state=active]:text-black text-zinc-400"
            data-testid="performance-tab"
          >
            Performance
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search */}
      {activeTab === 'freelancers' && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            placeholder="Search freelancers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#121212] border-white/10 text-white placeholder:text-zinc-500"
            data-testid="search-freelancers-input"
          />
        </div>
      )}

      {/* Freelancers Grid */}
      {activeTab === 'freelancers' && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="freelancers-grid">
          {filteredFreelancers.map(freelancer => (
            <Card 
              key={freelancer.id} 
              className="bg-[#121212] border-white/10 card-hover cursor-pointer"
              onClick={() => handleFreelancerClick(freelancer)}
              data-testid={`freelancer-card-${freelancer.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-full bg-purple-500/20 flex items-center justify-center shrink-0">
                    <User className="h-6 w-6 text-purple-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="text-white font-medium truncate">{freelancer.name}</h3>
                      <Badge variant="outline" className="bg-purple-500/10 text-purple-400 border-purple-500/20 capitalize shrink-0">
                        {freelancer.speciality}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-zinc-500 mt-1">
                      <Mail className="h-3 w-3" />
                      <span className="truncate">{freelancer.email}</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                  <div className="p-2 bg-[#0A0A0A] rounded">
                    <p className="text-lg font-semibold text-white">{freelancer.active_projects}</p>
                    <p className="text-[10px] text-zinc-500">Active</p>
                  </div>
                  <div className="p-2 bg-[#0A0A0A] rounded">
                    <p className={`text-lg font-semibold ${getReliabilityColor(freelancer.reliability_score)}`}>
                      {freelancer.reliability_score}%
                    </p>
                    <p className="text-[10px] text-zinc-500">Reliable</p>
                  </div>
                  <div className="p-2 bg-[#0A0A0A] rounded">
                    <div className="flex items-center justify-center gap-0.5">
                      {[1, 2, 3, 4, 5].map(star => (
                        <Star 
                          key={star} 
                          className={`h-3 w-3 ${star <= (freelancer.internal_rating || 0) ? 'text-[#F5A623] fill-[#F5A623]' : 'text-zinc-600'}`}
                        />
                      ))}
                    </div>
                    <p className="text-[10px] text-zinc-500">Rating</p>
                  </div>
                </div>

                {freelancer.rate_per_project && (
                  <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
                    <span className="text-xs text-zinc-500">Rate per project</span>
                    <span className="text-sm text-[#F5A623] font-medium">{formatCurrency(freelancer.rate_per_project)}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}

          {filteredFreelancers.length === 0 && (
            <div className="col-span-3 text-center py-12 text-zinc-500">
              <User className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No freelancers found</p>
            </div>
          )}
        </div>
      )}

      {/* Assignments List */}
      {activeTab === 'assignments' && (
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-4 text-xs text-zinc-500 uppercase tracking-wider font-medium">Project</th>
                    <th className="text-left p-4 text-xs text-zinc-500 uppercase tracking-wider font-medium">Freelancer</th>
                    <th className="text-left p-4 text-xs text-zinc-500 uppercase tracking-wider font-medium">Committed Date</th>
                    <th className="text-left p-4 text-xs text-zinc-500 uppercase tracking-wider font-medium">Days Left</th>
                    <th className="text-left p-4 text-xs text-zinc-500 uppercase tracking-wider font-medium">Status</th>
                    <th className="text-left p-4 text-xs text-zinc-500 uppercase tracking-wider font-medium">Payment</th>
                    <th className="text-left p-4 text-xs text-zinc-500 uppercase tracking-wider font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {assignments.map(assignment => (
                    <tr key={assignment.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`assignment-row-${assignment.id}`}>
                      <td className="p-4">
                        <p className="text-white font-medium">{assignment.project_title}</p>
                        <p className="text-xs text-zinc-500">{assignment.client_name}</p>
                      </td>
                      <td className="p-4 text-zinc-400">{assignment.freelancer_name}</td>
                      <td className="p-4 text-zinc-400">{formatDate(assignment.committed_delivery_date)}</td>
                      <td className="p-4">
                        {assignment.days_remaining !== null ? (
                          <span className={assignment.days_remaining < 0 ? 'text-rose-400' : 'text-white'}>
                            {assignment.days_remaining < 0 ? `${Math.abs(assignment.days_remaining)}d late` : `${assignment.days_remaining}d`}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="p-4">
                        <Badge variant="outline" className={getStatusBadge(assignment.status)}>
                          {assignment.status.replace(/_/g, ' ')}
                        </Badge>
                      </td>
                      <td className="p-4">
                        {assignment.agreed_amount != null && (
                          <div>
                            <p className="text-white text-sm">{formatCurrency(assignment.amount_paid)} / {formatCurrency(assignment.agreed_amount)}</p>
                            <Badge variant="outline" className={`text-xs ${
                              assignment.payment_status === 'paid' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                              assignment.payment_status === 'partial' ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' :
                              'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'
                            }`}>
                              {assignment.payment_status}
                            </Badge>
                          </div>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex flex-col gap-1.5">
                          {assignment.status === 'in_progress' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-xs h-7"
                              onClick={() => handleUpdateAssignmentStatus(assignment.id, 'delivered')}
                              data-testid={`mark-delivered-${assignment.id}`}
                            >
                              Mark Delivered
                            </Button>
                          )}
                          {isAdmin && assignment.payment_status !== 'paid' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-[#F5A623]/30 text-[#F5A623] hover:bg-[#F5A623]/10 text-xs h-7"
                              onClick={() => {
                                setSelectedAssignment(assignment);
                                setShowPaymentDialog(true);
                              }}
                              data-testid={`add-payment-${assignment.id}`}
                            >
                              <IndianRupee className="h-3 w-3 mr-1" /> Add Payment
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {assignments.length === 0 && (
              <div className="text-center py-12 text-zinc-500">
                <FolderKanban className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No assignments yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Performance Tab */}
      {activeTab === 'performance' && (
        <div className="space-y-6" data-testid="performance-section">
          {/* Summary Cards */}
          {performanceData.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-semibold text-white">{performanceData.length}</p>
                  <p className="text-xs text-zinc-500 mt-1">Total Freelancers</p>
                </CardContent>
              </Card>
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-semibold text-emerald-400">
                    {performanceData.filter(f => f.on_time_rate !== null && f.on_time_rate >= 90).length}
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">90%+ On-Time</p>
                </CardContent>
              </Card>
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-semibold text-white">
                    {performanceData.reduce((acc, f) => acc + f.total_completed, 0)}
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">Total Delivered</p>
                </CardContent>
              </Card>
              <Card className="bg-[#121212] border-white/10">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-semibold text-[#F5A623]">
                    {formatCurrency(performanceData.reduce((acc, f) => acc + (f.total_paid || 0), 0))}
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">Total Spent</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Ranking Table */}
          <Card className="bg-[#121212] border-white/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-white flex items-center gap-2 text-base">
                <Trophy className="h-5 w-5 text-[#F5A623]" /> Freelancer Ranking
              </CardTitle>
              <p className="text-xs text-zinc-500">Sorted by reliability score, completed projects, and rating</p>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-4 text-xs text-zinc-500 uppercase tracking-wider font-medium w-12">#</th>
                      <th className="text-left p-4 text-xs text-zinc-500 uppercase tracking-wider font-medium">Freelancer</th>
                      <th className="text-center p-4 text-xs text-zinc-500 uppercase tracking-wider font-medium">On-Time Rate</th>
                      <th className="text-center p-4 text-xs text-zinc-500 uppercase tracking-wider font-medium">Rating</th>
                      <th className="text-center p-4 text-xs text-zinc-500 uppercase tracking-wider font-medium">Completed</th>
                      <th className="text-center p-4 text-xs text-zinc-500 uppercase tracking-wider font-medium">Active</th>
                      <th className="text-center p-4 text-xs text-zinc-500 uppercase tracking-wider font-medium">Cost/Project</th>
                      <th className="text-center p-4 text-xs text-zinc-500 uppercase tracking-wider font-medium">Avg Days</th>
                      <th className="text-center p-4 text-xs text-zinc-500 uppercase tracking-wider font-medium">Total Paid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {performanceData.map(f => (
                      <tr key={f.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`perf-row-${f.id}`}>
                        <td className="p-4">
                          <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold ${
                            f.rank === 1 ? 'bg-[#F5A623]/20 text-[#F5A623]' :
                            f.rank === 2 ? 'bg-zinc-400/20 text-zinc-300' :
                            f.rank === 3 ? 'bg-amber-700/20 text-amber-600' :
                            'bg-white/5 text-zinc-500'
                          }`}>
                            {f.rank}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-full bg-purple-500/20 flex items-center justify-center shrink-0">
                              <User className="h-4 w-4 text-purple-400" />
                            </div>
                            <div>
                              <p className="text-white font-medium">{f.name}</p>
                              <p className="text-xs text-zinc-500 capitalize">{f.speciality}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 text-center">
                          {f.on_time_rate !== null ? (
                            <div>
                              <span className={`text-sm font-semibold ${
                                f.on_time_rate >= 90 ? 'text-emerald-400' :
                                f.on_time_rate >= 70 ? 'text-yellow-400' :
                                'text-rose-400'
                              }`}>
                                {f.on_time_rate}%
                              </span>
                              <p className="text-[10px] text-zinc-600">{f.on_time_count} on-time, {f.late_count} late</p>
                            </div>
                          ) : (
                            <span className="text-xs text-zinc-600">No data</span>
                          )}
                        </td>
                        <td className="p-4 text-center">
                          <div className="flex items-center justify-center gap-0.5">
                            {[1, 2, 3, 4, 5].map(star => (
                              <Star 
                                key={star} 
                                className={`h-3 w-3 ${star <= (f.internal_rating || 0) ? 'text-[#F5A623] fill-[#F5A623]' : 'text-zinc-700'}`}
                              />
                            ))}
                          </div>
                        </td>
                        <td className="p-4 text-center text-white font-medium">{f.total_completed}</td>
                        <td className="p-4 text-center text-zinc-400">{f.active_projects}</td>
                        <td className="p-4 text-center text-zinc-400">{f.cost_per_project ? formatCurrency(f.cost_per_project) : '-'}</td>
                        <td className="p-4 text-center text-zinc-400">{f.avg_delivery_days !== null ? `${f.avg_delivery_days}d` : '-'}</td>
                        <td className="p-4 text-center text-[#F5A623] font-medium">{f.total_paid ? formatCurrency(f.total_paid) : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {performanceData.length === 0 && (
                <div className="text-center py-12 text-zinc-500">
                  <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No performance data yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Freelancer Detail Sheet */}
      <Sheet open={showDetailSheet} onOpenChange={setShowDetailSheet}>
        <SheetContent className="bg-[#121212] border-l border-white/10 w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-white">Freelancer Profile</SheetTitle>
          </SheetHeader>
          
          {selectedFreelancer && (
            <div className="mt-6 space-y-6">
              {/* Profile Info */}
              <div className="flex items-start gap-4">
                <div className="h-16 w-16 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <User className="h-8 w-8 text-purple-400" />
                </div>
                <div>
                  <h2 className="text-xl text-white font-medium">{selectedFreelancer.name}</h2>
                  <Badge variant="outline" className="bg-purple-500/10 text-purple-400 border-purple-500/20 capitalize mt-1">
                    {selectedFreelancer.speciality}
                  </Badge>
                </div>
              </div>

              {/* Contact */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-zinc-400">
                  <Mail className="h-4 w-4" />
                  <span>{selectedFreelancer.email}</span>
                </div>
                {selectedFreelancer.phone && (
                  <div className="flex items-center gap-2 text-zinc-400">
                    <Phone className="h-4 w-4" />
                    <span>{selectedFreelancer.phone}</span>
                  </div>
                )}
                {selectedFreelancer.portfolio_link && (
                  <div className="flex items-center gap-2 text-zinc-400">
                    <Link className="h-4 w-4" />
                    <a href={selectedFreelancer.portfolio_link} target="_blank" rel="noopener noreferrer" className="text-[#F5A623] hover:underline">
                      Portfolio
                    </a>
                  </div>
                )}
              </div>

              {/* Rating */}
              <div className="p-4 bg-[#0A0A0A] rounded border border-white/5">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Internal Rating</p>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUpdateRating(selectedFreelancer.id, star);
                      }}
                      className="p-1 hover:scale-110 transition-transform"
                    >
                      <Star 
                        className={`h-6 w-6 ${star <= (selectedFreelancer.internal_rating || 0) ? 'text-[#F5A623] fill-[#F5A623]' : 'text-zinc-600'}`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-[#0A0A0A] rounded border border-white/5">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Reliability</p>
                  <p className={`text-2xl font-semibold ${getReliabilityColor(selectedFreelancer.reliability_score)}`}>
                    {selectedFreelancer.reliability_score}%
                  </p>
                </div>
                <div className="p-4 bg-[#0A0A0A] rounded border border-white/5">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Projects</p>
                  <p className="text-2xl font-semibold text-white">{selectedFreelancer.total_projects}</p>
                  <p className="text-xs text-zinc-500">{selectedFreelancer.completed} completed, {selectedFreelancer.late} late</p>
                </div>
              </div>

              {/* Performance Analytics */}
              {freelancerPerf && (
                <div className="p-4 bg-[#0A0A0A] rounded border border-white/5" data-testid="freelancer-performance-detail">
                  <div className="text-xs text-zinc-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <TrendingUp className="h-3 w-3" /> Performance Analytics
                    {freelancerPerf.rank && (
                      <Badge variant="outline" className="bg-[#F5A623]/10 text-[#F5A623] border-[#F5A623]/20 ml-auto text-xs">
                        Rank #{freelancerPerf.rank}
                      </Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-[#121212] rounded">
                      <p className="text-xs text-zinc-500">On-Time Rate</p>
                      <p className={`text-lg font-semibold ${
                        freelancerPerf.on_time_rate === null ? 'text-zinc-600' :
                        freelancerPerf.on_time_rate >= 90 ? 'text-emerald-400' :
                        freelancerPerf.on_time_rate >= 70 ? 'text-yellow-400' : 'text-rose-400'
                      }`}>
                        {freelancerPerf.on_time_rate !== null ? `${freelancerPerf.on_time_rate}%` : 'N/A'}
                      </p>
                      {freelancerPerf.on_time_rate !== null && (
                        <p className="text-[10px] text-zinc-600">{freelancerPerf.on_time_count} on-time / {freelancerPerf.late_count} late</p>
                      )}
                    </div>
                    <div className="p-3 bg-[#121212] rounded">
                      <p className="text-xs text-zinc-500">Cost/Project</p>
                      <p className="text-lg font-semibold text-[#F5A623]">
                        {freelancerPerf.cost_per_project ? formatCurrency(freelancerPerf.cost_per_project) : 'N/A'}
                      </p>
                    </div>
                    <div className="p-3 bg-[#121212] rounded">
                      <p className="text-xs text-zinc-500">Avg Delivery</p>
                      <p className="text-lg font-semibold text-white">
                        {freelancerPerf.avg_delivery_days !== null ? `${freelancerPerf.avg_delivery_days} days` : 'N/A'}
                      </p>
                    </div>
                    <div className="p-3 bg-[#121212] rounded">
                      <p className="text-xs text-zinc-500">Total Paid</p>
                      <p className="text-lg font-semibold text-emerald-400">
                        {freelancerPerf.total_paid ? formatCurrency(freelancerPerf.total_paid) : 'N/A'}
                      </p>
                      {freelancerPerf.total_agreed > 0 && freelancerPerf.total_agreed !== freelancerPerf.total_paid && (
                        <p className="text-[10px] text-zinc-600">of {formatCurrency(freelancerPerf.total_agreed)} agreed</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Rate */}
              {selectedFreelancer.rate_per_project && (
                <div className="p-4 bg-[#0A0A0A] rounded border border-white/5">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Rate per Project</p>
                  <p className="text-2xl font-semibold text-[#F5A623]">{formatCurrency(selectedFreelancer.rate_per_project)}</p>
                </div>
              )}

              {/* Payment Summary */}
              {freelancerPayments && (
                <div className="p-4 bg-[#0A0A0A] rounded border border-white/5">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Payment Summary</p>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-lg font-semibold text-white">{formatCurrency(freelancerPayments.summary.total_earned)}</p>
                      <p className="text-xs text-zinc-500">Total Earned</p>
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-emerald-400">{formatCurrency(freelancerPayments.summary.total_paid)}</p>
                      <p className="text-xs text-zinc-500">Paid</p>
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-amber-400">{formatCurrency(freelancerPayments.summary.total_pending)}</p>
                      <p className="text-xs text-zinc-500">Pending</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Recent Payments */}
              {freelancerPayments?.payments?.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Recent Payments</p>
                  <div className="space-y-2">
                    {freelancerPayments.payments.slice(0, 5).map(payment => (
                      <div key={payment.id} className="p-3 bg-[#0A0A0A] rounded border border-white/5 flex items-center justify-between">
                        <div>
                          <p className="text-white text-sm">{payment.project_title}</p>
                          <p className="text-xs text-zinc-500">{formatDate(payment.payment_date)}</p>
                        </div>
                        <p className="text-emerald-400 font-medium">{formatCurrency(payment.amount)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Add Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Add Payment</DialogTitle>
          </DialogHeader>
          {selectedAssignment && (
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-[#0A0A0A] rounded border border-white/5">
                <p className="text-white font-medium">{selectedAssignment.project_title}</p>
                <p className="text-xs text-zinc-500">
                  {selectedAssignment.freelancer_name} — Agreed: {formatCurrency(selectedAssignment.agreed_amount)}, Paid: {formatCurrency(selectedAssignment.amount_paid)}
                </p>
              </div>

              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Amount (₹) *</Label>
                <Input
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                  placeholder="5000"
                  data-testid="payment-amount-input"
                />
              </div>

              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Payment Date *</Label>
                <Input
                  type="date"
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm({...paymentForm, payment_date: e.target.value})}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                  data-testid="payment-date-input"
                />
              </div>

              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Notes</Label>
                <Input
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                  placeholder="UPI / Bank transfer / Cash"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowPaymentDialog(false)} className="border-white/10 text-white hover:bg-white/5">
                  Cancel
                </Button>
                <Button 
                  onClick={handleAddPayment}
                  className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black"
                  data-testid="submit-payment-btn"
                >
                  Record Payment
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FreelancersPage;
